import dayjs, { Dayjs } from "dayjs"
import querystring from "query-string"

import { API_ROOT } from "./api/urls"
import {
  IS_WHITE_LABEL,
  LabelPaths,
  LABELS,
  TIME_FORMATS,
  WHITE_LABEL_LABELS,
} from "./constants"
import { HTML_BLOCK_TAGS } from "./constants"
import { InternalTime, OptionType } from "./types/sharedTypes"
import { AppLauncher } from "@capacitor/app-launcher"
import { Browser } from "@capacitor/browser"
import { Capacitor, PluginListenerHandle } from "@capacitor/core"
import { Device } from "@capacitor/device"

import { DepartmentResponse } from "./redux/api/departments/types"
import { DeskReservation } from "./redux/api/deskReservations/types"
import { AssetReservation } from "./redux/asset_reservation/types"
import { BuildingsResponse } from "./redux/buildings/types"
import { BrandingOption } from "./redux/printer_settings/types"
import { BrandingResponse } from "./redux/visitor_registration/types"

import { GooglePlacesSelectType } from "./components/advanced/GooglePlacesSelect/types"

import defaultLandscapeLogoUrl from "./assets/images/logo-landscape.svg?url"

export const BLANK_STR_REGEX = /^\s*$/
export const EMAIL_REGEX = /^\S+@\S+\.\S+$/
export const COLOR_HEX_REGEX = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/
export const IP_ADDRESS_REGEX = /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/

/** Accepts 12 hex chars or colon-separated MAC (e.g. AA:BB:CC:DD:EE:FF). */
export const MAC_ADDRESS_REGEX =
  /^(?:[0-9A-Fa-f]{12}|([0-9A-Fa-f]{2}:){5}[0-9A-Fa-f]{2})$/

export const URL_REGEX = /^(https?:\/\/)([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/.*)?$/

/** MAC as 12 hex digits, colons optional (e.g. Bluetooth device identity). */
export function isValidMacString(s: string): boolean {
  const normalized = (s ?? "").replace(/:/g, "").trim()
  return /^[0-9a-fA-F]{12}$/.test(normalized)
}

const FORCE_MODE_LOCAL_KEY = "force_device_render"
const { parse, stringify } = querystring

export const delay = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms))

export const waitForAnimationFrame = (): Promise<void> =>
  new Promise((resolve) => {
    requestAnimationFrame(() => {
      resolve()
    })
  })

/** Two animation frames so layout from a prior DOM update is measurable (e.g. offsetWidth). */
export const waitForLayoutPaint = (): Promise<void> =>
  waitForAnimationFrame().then(() => waitForAnimationFrame())

// Bound an `await` to a deadline; rejects with `Error(timeoutErrorMessage)` if `work` does not settle in time.
export const raceWithTimeout = <T>(
  work: Promise<T>,
  ms: number,
  timeoutErrorMessage: string,
): Promise<T> =>
  new Promise((resolve, reject) => {
    let settled = false
    const timer = setTimeout(() => {
      if (settled) {
        return
      }

      settled = true
      reject(new Error(timeoutErrorMessage))
    }, ms)
    work.then(
      (value) => {
        if (settled) {
          return
        }

        settled = true
        clearTimeout(timer)
        resolve(value)
      },
      (err) => {
        if (settled) {
          return
        }

        settled = true
        clearTimeout(timer)
        reject(err)
      },
    )
  })

export const isDev = () => import.meta.env.DEV

export const isProd = () => import.meta.env.PROD

export const countTrueArrayValues = (values: boolean[]) =>
  values.filter(Boolean).length

export const getInitialsFromFullName = (fullName: string): string => {
  const parts = fullName.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) {
    return ""
  }

  const first = parts[0][0] ?? ""
  const last = parts.length > 1 ? (parts[parts.length - 1][0] ?? "") : ""
  return `${first}${last}`.toUpperCase()
}

export const getVisitorDisplayName = (
  fullName: string,
  t: (key: string) => string,
): string => {
  const trimmedName = fullName.trim()

  if (!trimmedName) {
    return t("desktop.manage.desk_booking.visitor")
  }

  return `${trimmedName} ${t("desktop.manage.desk_booking.visitor_suffix")}`
}

export const pluralize = (number: number) => {
  switch (number % 10) {
    case 1:
      if (number !== 11) {
        return `${number}st`
      } else {
        return `${number}th`
      }

    case 2:
      if (number !== 12) {
        return `${number}nd`
      } else {
        return `${number}th`
      }

    case 3:
      if (number !== 13) {
        return `${number}rd`
      }

      return `${number}th`
    default:
      return `${number}th`
  }
}

export function colorFromString(str: string) {
  if (str === null) {
    str = "default"
  }

  const seed = xmur3(str)
  const rand = sfc32(seed(), seed(), seed(), seed())
  const angle = Math.ceil(rand() * 360)
  return `hsl(${angle}, 50%, 66%)`
}

export function sfc32(a: number, b: number, c: number, d: number) {
  return function () {
    a >>>= 0
    b >>>= 0
    c >>>= 0
    d >>>= 0
    let t = (a + b) | 0
    a = b ^ (b >>> 9)
    b = (c + (c << 3)) | 0
    c = (c << 21) | (c >>> 11)
    d = (d + 1) | 0
    t = (t + d) | 0
    c = (c + t) | 0
    return (t >>> 0) / 4294967296
  }
}

export function xmur3(str: string) {
  let h = 1779033703 ^ str.length
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 3432918353)
    h = (h << 13) | (h >>> 19)
  }

  return function () {
    h = Math.imul(h ^ (h >>> 16), 2246822507)
    h = Math.imul(h ^ (h >>> 13), 3266489909)
    return (h ^= h >>> 16) >>> 0
  }
}

const date = new Date()

export const is12Hour = () => {
  const localeTimeString = date.toLocaleTimeString(dayjs.locale(), {
    hour: "numeric",
    minute: "numeric",
  })
  return /AM|PM/.test(localeTimeString)
}

/**
 * Returns the user time format based on the company time format.
 * @param companyTimeFormat - The company time format.
 * @returns The user time format.
 */
export const userTimeFormat = (companyTimeFormat?: string | null) => {
  if (
    companyTimeFormat &&
    TIME_FORMATS[companyTimeFormat as keyof typeof TIME_FORMATS]
  ) {
    return TIME_FORMATS[companyTimeFormat as keyof typeof TIME_FORMATS].format
  }

  return is12Hour() ? TIME_FORMATS["2"].format : TIME_FORMATS["1"].format
} // Example: "02:00 PM", "09:30 AM", "12:45 PM" / "14:00", "09:30", "12:45"

export const internalTimeFormat = () => "HH:mm"

export const roundToClosest30Minutes = (date: Dayjs) => {
  const minutes = date.minute()
  if (minutes < 15) {
    return date.startOf("hour")
  } else if (minutes < 45) {
    return date.startOf("hour").add(30, "minute")
  } else {
    return date.startOf("hour").add(1, "hour")
  }
}

/**
 * Converts a date to an internal time string (HH:mm).
 * @param date - The date to convert.
 * @returns The internal time string (HH:mm).
 */
export const toInternalTime = (date: Dayjs | string): InternalTime =>
  (typeof date === "string" ? dayjs(date) : date).format(
    internalTimeFormat(),
  ) as InternalTime

/**
 * Ensures a time value is in InternalTime format (HH:mm).
 * Returns "00:00" as fallback if input is invalid.
 * @param time - The time value to ensure.
 * @returns The time value in InternalTime format (HH:mm).
 */
export const ensureInternalTime = (time: string): InternalTime => {
  if (isInternalTime(time)) {
    return time
  }

  const converted = toInternalTime(time)

  if (!isInternalTime(converted)) {
    return "00:00" as InternalTime
  }

  return converted
}

/**
 * The backend sometimes stores times as HH:MM:SS (e.g. Django TimeField), while
 * our time pickers and the InternalTime type use HH:MM. These two helpers
 * convert between the backend representation and InternalTime.
 */
export const timeFieldToInternalTime = (
  time?: string | null,
): InternalTime | undefined =>
  time ? (time.slice(0, 5) as InternalTime) : undefined

export const internalTimeToTimeField = (time: InternalTime): string =>
  `${time}:00`

export const geocodeByPlaceId = (placeId: string) => {
  const { maps } = (window as any).google

  const geocoder: google.maps.Geocoder = new maps.Geocoder()
  const { OK } = maps.GeocoderStatus

  return new Promise<google.maps.GeocoderResult[] | null>((resolve, reject) => {
    geocoder.geocode({ placeId }, (results, status) => {
      if (status !== OK) {
        return reject(status)
      }

      return resolve(results)
    })
  })
}

export const isBlank = (str?: string) => {
  return !str || BLANK_STR_REGEX.test(str)
}

export const isEmail = (str: string) => {
  return EMAIL_REGEX.test(str)
}

export const isValidURL = (url?: string) => {
  if (!url) {
    return false
  }

  return URL_REGEX.test(url)
}

const isNumber = function isNumber(value: any) {
  return typeof value === "number" && isFinite(value)
}

export const isInternalTime = (value: unknown): value is InternalTime => {
  if (typeof value !== "string") {
    return false
  }

  if (!value.includes(":")) {
    return false
  }

  const [hours, minutes] = value.split(":")
  if (!isNumber(Number(hours)) || !isNumber(Number(minutes))) {
    return false
  }

  return true
}

export const asInternalTime = (value: unknown): InternalTime | undefined =>
  isInternalTime(value) ? value : undefined

/**
 * Compering two internal times.
 * @param a {InternalTime} - internal time.
 * @param b {InternalTime} - internal time.
 * @returns true when first time si before second time.
 */
export const isBefore = (a: InternalTime, b: InternalTime) => {
  const [aHours, aMinutes] = a.split(":")
  const [bHours, bMinutes] = b.split(":")

  if (aHours < bHours) {
    return true
  }

  if (bHours < aHours) {
    return false
  }

  return aMinutes <= bMinutes
}

export const getLaterInternalTime = (a: InternalTime, b: InternalTime) =>
  isBefore(a, b) ? b : a

/**
 * Creates a dayjs object with the given date and time.
 * @param date - The date to create the dayjs object from.
 * @param time - The time to set the dayjs object to.
 * @param timezone - Optional timezone for timezone-aware creation.
 *
 * Note: We always include time when using dayjs.tz() to avoid DST issues.
 * On DST transition days, midnight might not exist (e.g., clocks jump 2 AM → 3 AM),
 * so date-only strings like dayjs.tz("2025-03-30", "Europe/Ljubljana") can give wrong results.
 */
export const createDateTime = (
  date: Dayjs,
  time: InternalTime,
  timezone?: string,
): Dayjs => {
  // Use the calendar date as-is (don't convert to target timezone)
  const dateStr = date.format("YYYY-MM-DD")

  if (timezone) {
    return dayjs.tz(`${dateStr} ${time}`, timezone)
  }

  return dayjs(`${dateStr} ${time}`)
}

/**
 * Centralized utility for formatting reservation dates with timezone awareness.
 * This ensures all reservation types (desk, asset, room, invite, visit, appointment) use consistent timezone formatting.
 */

export type ReservationTimeRange = {
  start: InternalTime
  end: InternalTime
}

export type ReservationDateFormatOptions = {
  date: string | Date | Dayjs
  timeRange: ReservationTimeRange
  timezone: string
  isAllDay?: boolean
}

/**
 * Use this function when CREATING reservations.
 * Times are interpreted in building's timezone, then converted to UTC for storage.
 *
 * For DISPLAYING reservations, use `useTimeFormat` hook (see src/hooks/useTimeFormat.ts).
 *
 * @param options - Date, time range, and timezone information.
 * @returns Object with formatted start/end times (as ISO strings) and timezone.
 */
export const formatReservationDates = ({
  date,
  timeRange,
  timezone,
  isAllDay = false,
}: ReservationDateFormatOptions) => {
  const baseDate = dayjs(date)
  const start = createDateTime(baseDate, timeRange.start, timezone)
  const end = createDateTime(baseDate, timeRange.end, timezone)

  if (isAllDay) {
    return {
      start: dayjs.tz(`${date} 12:00`, timezone).startOf("day").toISOString(),
      end: dayjs
        .tz(`${date} 12:00`, timezone)
        .add(1, "day")
        .startOf("day")
        .toISOString(),
      tz: timezone,
    }
  }

  return {
    start: start.toISOString(),
    end: end.toISOString(),
    tz: timezone,
  }
}

export const calculatePercentAmount = (totalAmount: number, percent: number) =>
  Math.floor((totalAmount * percent) / 100)

type HasId = {
  id: string
}

export const equalArraysById = (a: HasId[], b: HasId[]) => {
  if (a.length !== b.length) {
    return false
  } else if (a.every((obj, index) => obj.id === b[index].id)) {
    return true
  } else {
    return false
  }
}

type HasName = {
  name: string
}

export function nameComparator(a: HasName, b: HasName) {
  if (!a || !b || !a.name || !b.name) {
    return 0
  }

  return a.name.localeCompare(b.name, undefined, { numeric: true })
}

export const countDesksInBuildings = (buildings: BuildingsResponse) => {
  let numOfDesks = 0

  buildings.results.forEach((building) => {
    numOfDesks += building.desks_count || 0
  })

  return numOfDesks
}

export const countFloorsInBuildings = (buildings: BuildingsResponse) => {
  let numOfFloors = 0

  buildings.results.forEach((building) => {
    numOfFloors += building.floors_count || 0
  })

  return numOfFloors
}

/**
 * Parses a query string and returns an object with the default values.
 * @param queryString - The query string to parse.
 * @param defaultValues - The default values to use if the query string is empty.
 * @returns An object with the parsed query string.
 */
export const parseQueryWithDefault = <T>(
  queryString: string,
  defaultValues: T,
): T => {
  const params = parse(queryString, {
    parseNumbers: true,
  })
  const result: { [x: string]: unknown } = {}

  if (!params) {
    return defaultValues
  }

  Object.keys(params).forEach((key) => {
    const value = params[key]

    if (value === undefined || value === null) {
      return
    }

    result[key] = value
  })

  return { ...defaultValues, ...result }
}

export const getDepartmentString = (departments?: DepartmentResponse[]) =>
  departments
    ? departments.map((d: DepartmentResponse) => d.name).join(", ")
    : ""

const characters =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789"

export const generateString = (length: number) => {
  let result = ""
  const charactersLength = characters.length
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength))
  }

  return result
}

export const getDirectionsLink = (
  directionsLink: boolean,
  address?: GooglePlacesSelectType,
) => {
  if (directionsLink && address?.address) {
    return `${
      import.meta.env.VITE_APP_GOOGLE_MAP_DIRECTION_URL
    }${encodeURIComponent(address.address)}`
  }
}

const testUserAgent = (expr: RegExp) => expr.test(window.navigator.userAgent)

const isIpad = () => {
  // iOS 12 and below
  if (testUserAgent(/iPad/i)) {
    return true
  }

  // iOS 13+
  if (testUserAgent(/Macintosh/i)) {
    return true
  }

  return false
}

const isAndroidTablet = () =>
  testUserAgent(/android|sink/i) && !testUserAgent(/mobile/i)

export const isTablet = () => {
  const width = window.innerWidth
  const height = window.innerHeight
  const smallest = Math.min(width, height)
  const largest = Math.max(width, height)

  if (!isBlank(import.meta.env.VITE_APP_FORCE_DEVICE_RENDER)) {
    return import.meta.env.VITE_APP_FORCE_DEVICE_RENDER === "TABLET"
  }

  if (localStorage.getItem(FORCE_MODE_LOCAL_KEY) !== null) {
    return localStorage.getItem(FORCE_MODE_LOCAL_KEY) === "tablet"
  }

  return (
    Capacitor.isNativePlatform() &&
    (isIpad() ||
      isAndroidTablet() ||
      (smallest > 460 && smallest < 820 && largest > 780 && largest < 1400))
  )
}

export const isMobile = (params?: { ignoreWidth: boolean }) => {
  if (!isBlank(import.meta.env.VITE_APP_FORCE_DEVICE_RENDER)) {
    return import.meta.env.VITE_APP_FORCE_DEVICE_RENDER === "MOBILE"
  }

  if (localStorage.getItem(FORCE_MODE_LOCAL_KEY) !== null) {
    return localStorage.getItem(FORCE_MODE_LOCAL_KEY) === "mobile"
  }

  if (params?.ignoreWidth) {
    return Capacitor.isNativePlatform()
  }

  return Capacitor.isNativePlatform() || window.innerWidth < 1024
}

export const forceMode = (mode?: "tablet" | "mobile") => {
  if (mode) {
    localStorage.setItem(FORCE_MODE_LOCAL_KEY, mode)
  } else {
    localStorage.removeItem(FORCE_MODE_LOCAL_KEY)
  }
}

/**
 * This function should be used in case we need native
 * business logic and not native functionality.
 *
 * CAREFUL:
 *
 * The isNative() function will return true while
 * Capacitor.isNativePlatform() could return false in
 * certain situations, like if we force the tablet
 * render with the env variable VITE_APP_FORCE_DEVICE_RENDER.
 *
 * For example, if you just need to make sure the app
 * is being ran on a native platform because you need to
 * access the camera for example, you still need to perform
 * the check with Capacitor.isNativePlatform().
 */
export const isNative = () => isTablet() || isMobile({ ignoreWidth: true })

/**
 * Resolve the device's UI language. On native we read it via Capacitor
 * instead of `navigator.language`, because in a native WebView
 * `navigator.language` reflects the app bundle's declared localizations
 * (English-only here) rather than the actual device setting, so a German
 * device would otherwise report English.
 */
export const getDeviceLanguage = async (): Promise<string> => {
  if (Capacitor.isNativePlatform()) {
    try {
      const { value } = await Device.getLanguageTag()

      return value
    } catch {
      return navigator.language
    }
  }

  return navigator.language
}

/**
 * Updates the URL without causing a navigation reload.
 * This function modifies the current browser history state by updating the URL with the specified path, filters, and optional hash.
 * It uses the `window.history.replaceState` method to update the browser's history without triggering a page reload or navigation.
 * If the `triggerPopState` flag is set to `true`, it also dispatches a `popstate` event with the serialized filters state.
 *
 * @param pathname - The new pathname to update in the URL (e.g., "/home").
 * @param filters - An object containing the filters to append as query parameters to the URL.
 * @param hash - An optional hash to append to the URL (e.g., "#section").
 * @param triggerPopState - A boolean flag to trigger the `popstate` event manually. If set to `true`, a `popstate` event will be dispatched with the serialized filters state.
 */
export const updateHistory = <Filters extends Record<string, any>>(
  pathname: string,
  filters: Filters,
  hash?: string,
  triggerPopState: boolean = false,
) => {
  const url = `${pathname}?${stringify(filters)}${hash ? hash : ""}`

  window.history.replaceState(null, document.title, url)

  if (triggerPopState) {
    window.dispatchEvent(
      new PopStateEvent("popstate", {
        state: null,
      }),
    )
  }
}

export const prettifyUrlValue = (location: string) => {
  const URL_REGEX = /[	\-_:]+/g // eslint-disable-line

  return (
    location.charAt(0).toUpperCase() + location.slice(1).replace(URL_REGEX, " ")
  )
}

export const openInAppBrowser = async (url: string) => {
  await Browser.open({ url })
}

/** Schemes that hand off to another app and cannot be shown in an in-app browser. */
const OS_HANDLED_URL_SCHEMES = ["mailto:", "tel:", "sms:"]

const isOsHandledUrl = (url: string) =>
  OS_HANDLED_URL_SCHEMES.some((scheme) => url.startsWith(scheme))

/**
 * Opens a link outside of the app without unloading it. Letting the WebView
 * follow an external href navigates away from the bundled app, so iOS shows a
 * white screen once the user returns from Safari or Mail.
 */
export const openExternalUrl = async (url: string) => {
  if (!Capacitor.isNativePlatform()) {
    if (isOsHandledUrl(url)) {
      window.location.href = url
      return
    }

    window.open(url, "_blank", "noopener,noreferrer")
    return
  }

  if (isOsHandledUrl(url)) {
    await AppLauncher.openUrl({ url })
    return
  }

  await Browser.open({ url })
}

/**
 * Opens a new browser window with the specified URL.
 *
 * @param url - The URL to open in the new window.
 * @param target - The target attribute of the window (optional, defaults to "_blank").
 */
export const openBrowserWindow = async (
  url: string,
  target: string = "_blank",
) => {
  window.open(url, target)
}

/**
 * @param hexColor Hex value format: #ffffff or ffffff.
 * @param decimal lighten or darken decimal value, example 0.5 to lighten by 50% or 1.5 to darken by 50%.
 */
export const shadeColor = (hexColor: string, decimal: number): string => {
  const base = hexColor.startsWith("#") ? 1 : 0

  let r = parseInt(hexColor.substring(base, 3), 16)
  let g = parseInt(hexColor.substring(base + 2, 5), 16)
  let b = parseInt(hexColor.substring(base + 4, 7), 16)

  r = Math.round(r / decimal)
  g = Math.round(g / decimal)
  b = Math.round(b / decimal)

  r = r < 255 ? r : 255
  g = g < 255 ? g : 255
  b = b < 255 ? b : 255

  return `#${r.toString(16).padStart(2, "0")}${g
    .toString(16)
    .padStart(2, "0")}${b.toString(16).padStart(2, "0")}`
}

export const recordToOptions = <T>(obj: Record<string, T>) =>
  Object.entries(obj).map(([key, label]) => ({
    value: key,
    label: label,
  }))

export const getOption = <Value = string>(
  options: OptionType<Value>[],
  value?: Value | null,
) => (value ? options.find((t) => t.value === value) : undefined)

/**
 * Converts the values of an object into options with both value and label properties.
 *
 * @param obj - The object whose values will be converted into options.
 * @returns An array of options with value and label properties.
 */
export const objectValuesToOptions = <T>(obj: Record<string, T>) =>
  Object.values(obj).map((label) => ({
    value: label,
    label,
  }))

/**
 * Converts an object to an array of options.
 * Each option in the resulting array contains a value and a label.
 *
 * @param obj - The object to convert to options.
 * @returns An array of options.
 */
export const objectToOptions = (
  obj: Record<string, { value: string; label: string }>,
) =>
  Object.values(obj).map((o) => ({
    value: o.value,
    label: o.label,
  }))

/**
 * Retrieves the label associated with the given key base on WHITE LABEL flag.
 *
 * @param key - The key to retrieve the label for.
 * @returns The label associated with the key, or the key itself if no label is found.
 */
export const getLabel = (key: LabelPaths): string => {
  const labels = IS_WHITE_LABEL ? WHITE_LABEL_LABELS : LABELS
  try {
    return getValueByPath(labels, key)
  } catch (e) {
    return key
  }
}

/**
 * Retrieves a value from an object using a dot-separated path.
 *
 * @param obj - The object to retrieve the value from.
 * @param path - The dot-separated path to the value.
 * @returns The value at the specified path, or undefined if not found.
 */
const getValueByPath = (obj: Record<string, any>, path: string): any => {
  const keys = path.split(".")
  const key = keys.shift()

  if (key && Object.prototype.hasOwnProperty.call(obj, key)) {
    const value = obj[key]

    if (keys.length === 0) {
      return value
    } else if (typeof value === "object") {
      return getValueByPath(value, keys.join("."))
    }
  }

  throw new Error(`Invalid path: ${path}`)
}

/**
 * Creates an array of numbers from 0 to the specified length.
 *
 * @param length - The length of the array to create.
 * @returns An array of numbers from 0 to length-1.
 */
export const createArray = (length: number) =>
  Array.from({ length }, (_, i) => i)

/**
 * Randomizes the order of elements in an array.
 *
 * @param array - The array to be randomized.
 * @returns A new array with the elements randomly shuffled.
 * @template ArrayType - The type of elements in the array.
 */
export const randomizeArray = <ArrayType>(array: ArrayType[]) =>
  [...array].sort(() => Math.random() - 0.5)

/**
 * Reads a value from the local storage based on the provided key.
 *
 * @param key - The key used to retrieve the value from the local storage.
 * @returns The value associated with the provided key, or null if the local storage is not available.
 */
export const readLocal = (key: string): string | null => {
  if (!window.localStorage) {
    return null
  }

  return window.localStorage.getItem(key)
}

/**
 * Writes a value to the local storage with the specified key.
 * @param key - The key to use for storing the value.
 * @param value - The value to be stored.
 */
export const writeLocal = (key: string, value: string): void => {
  if (!window.localStorage) {
    return
  }

  window.localStorage.setItem(key, value)
}

export const removePluginListeners = async (
  handle: Promise<PluginListenerHandle>,
) => {
  try {
    return await handle.then((handle) => handle.remove())
  } catch (e) {
    console.log("Something went wrong when removing a listener.")
  }
}

/**
 * The NFC library we use does not always return proper
 * strings, they may have weird characters at start that
 * String() or trim() can't pick up and remove. It
 * also strips away https.
 */
export const sanitizeUrl = (url: string) => {
  const regex = /^[a-zA-Z]/
  let loopPrevention = 0
  let result = url

  while (loopPrevention < 100 && !regex.test(result)) {
    result = result.substring(1)
    loopPrevention++
  }

  if (!result.startsWith("https://")) {
    result = `https://${result}`
  }

  return result
}

/**
 * Calculate the distance between two points using Euclidean distance formula.
 * @param x1 - The x-coordinate of the first point.
 * @param y1 - The y-coordinate of the first point.
 * @param x2 - The x-coordinate of the second point.
 * @param y2 - The y-coordinate of the second point.
 * @returns The distance between the two points.
 */
export const calculateDistance = (
  x1: number | undefined,
  y1: number | undefined,
  x2: number | undefined,
  y2: number | undefined,
) => {
  if (
    x1 === undefined ||
    y1 === undefined ||
    x2 === undefined ||
    y2 === undefined
  ) {
    return Infinity
  }

  return Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2)
}

/**
 * Safely compares two dates to check if they represent the same calendar day.
 * This function avoids timezone shifting issues that can occur with dayjs.isSame(*, "day")
 * when comparing dates across different timezones.
 *
 * @param date1 - First date to compare (Dayjs object, Date, or ISO string)
 * @param date2 - Second date to compare (Dayjs object, Date, or ISO string)
 * @returns True if both dates represent the same calendar day (YYYY-MM-DD)
 *
 * @example
 * // ✅ Safe timezone-aware comparison
 * isSameCalendarDay("2025-08-26T00:00:00+02:00", "2025-08-26T23:59:59-04:00") // true
 *
 * // ❌ Problematic (can fail across timezones)
 * // dayjs("2025-08-26T00:00:00+02:00").isSame(dayjs("2025-08-26T23:59:59-04:00"), "day") // false
 */
export const isSameCalendarDay = (
  date1: Dayjs | Date | string,
  date2: Dayjs | Date | string,
): boolean => {
  return dayjs(date1).format("YYYY-MM-DD") === dayjs(date2).format("YYYY-MM-DD")
}

/**
 * Checks if a given date falls on a weekend (Saturday or Sunday).
 * @param {dayjs.Dayjs | Date} date - The date to check. Can be a Day.js instance, or a Date object.
 * @returns {boolean} - Returns `true` if the date is a weekend, otherwise `false`.
 */
export const isWeekends = (date: dayjs.Dayjs | Date): boolean => {
  const day = dayjs(date).day() // 0 = Sunday, 6 = Saturday
  return day === 0 || day === 6
}

/**
 * The default prereservation period is 4 hours.
 */
export const defaultPrereservationPeriod = "P0DT04H00M00S"

/**
 * Parses ISO 8601 duration format and returns the total seconds.
 * Example: P0DT04H00M00S becomes 14400 (seconds).
 * @param isoDuration ISO 8601 duration string (e.g., P0DT04H00M00S).
 * @returns Total seconds as a number.
 */
export const parseISODurationToSeconds = (
  isoDuration?: string | null,
): number => {
  if (!isoDuration) {
    return 0
  }

  // Format: P[n]DT[n]H[n]M[n]S
  const daysMatch = isoDuration.match(/(\d+)D/)
  const hoursMatch = isoDuration.match(/(\d+)H/)
  const minutesMatch = isoDuration.match(/(\d+)M/)
  const secondsMatch = isoDuration.match(/(\d+)S/)

  const days = daysMatch ? parseInt(daysMatch[1], 10) : 0
  const hours = hoursMatch ? parseInt(hoursMatch[1], 10) : 0
  const minutes = minutesMatch ? parseInt(minutesMatch[1], 10) : 0
  const seconds = secondsMatch ? parseInt(secondsMatch[1], 10) : 0

  return days * 24 * 60 * 60 + hours * 60 * 60 + minutes * 60 + seconds
}

/**
 * Checks if a spot has pre-reservations based on the reservations, settings, and reference time.
 *
 * @param reservations Array of reservations with start and end times.
 * @param settings Application settings object containing prereservation period and timezone.
 * @param referenceTime Reference time to use for calculating upcoming reservations (timezone-aware).
 * @returns True if the spot has a pre-reservation, false otherwise.
 */
export const hasPreReservation = (
  reservations: Array<{
    start?: string | Date | null
    end?: string | Date | null
  }>,
  settings:
    | { desk_prereservation_period?: string | number | null; timezone: string }
    | undefined
    | null,
  referenceTime: Dayjs,
): boolean => {
  if (!settings?.desk_prereservation_period || reservations.length === 0) {
    return false
  }

  const time = referenceTime
  const prereservationPeriod = settings.desk_prereservation_period
  const prereservationSeconds =
    typeof prereservationPeriod === "number"
      ? prereservationPeriod
      : parseISODurationToSeconds(prereservationPeriod)

  return reservations.some((r) => {
    if (!r.start) {
      return false
    }

    const start = settings.timezone
      ? dayjs(r.start).tz(settings.timezone)
      : dayjs(r.start)

    return start > time && start.diff(time, "second") <= prereservationSeconds
  })
}

/**
 * Type for reservation-like objects with start, end, and optional timezone
 */
type ReservationTimeBounds = {
  start?: string | null
  end?: string | null
  tz?: string | null
}

/**
 * Checks if a reservation is currently active (current time is between start and end).
 * Uses reservation's timezone for accurate comparison.
 *
 * @param reservation - Object with start, end, and optional tz properties
 * @param now - Optional dayjs object for current time (defaults to dayjs())
 * @returns true if the reservation is currently active
 */
export const isReservationActive = (
  reservation: ReservationTimeBounds,
  now: Dayjs = dayjs(),
): boolean => {
  if (!reservation.start || !reservation.end) {
    return false
  }

  const tz = reservation.tz || dayjs.tz.guess()
  const start = dayjs(reservation.start).tz(tz)
  const end = dayjs(reservation.end).tz(tz)
  const nowInTz = now.tz(tz)
  return nowInTz.isAfter(start) && nowInTz.isBefore(end)
}

// Backend availability strings differ from UI status names; maintain stable mapping here.
export const DeskStatus = {
  AVAILABLE: "AVAILABLE",
  OCCUPIED: "OCCUPIED",
  UPCOMING: "UPCOMING",
  UNAVAILABLE: "UNAVAILABLE",
} as const
export type DeskStatus = (typeof DeskStatus)[keyof typeof DeskStatus]

// Maps backend availability status to UI desk status.
export const mapDeskAvailabilityToDeskStatus = (
  availability: string,
): DeskStatus => {
  switch (availability) {
    case "FREE":
      return DeskStatus.AVAILABLE
    case "BUSY":
      return DeskStatus.OCCUPIED
    case "PENDING":
      return DeskStatus.UPCOMING
    case "ASSIGNED_TO_DEPARTMENT":
    case "ASSIGNED_TO_USER":
    case "DISABLED":
    default:
      return DeskStatus.UNAVAILABLE
  }
}

/**
 * Reduces an availability response (desks or assets — both share the shape from
 * JOAN-10129) into a `{ id → DeskStatus }` map for the floor-plan color logic.
 */
export const buildAvailabilityStatusMap = <
  T extends { id: string; status: { availability: string } },
>(
  entries: T[],
): Record<string, DeskStatus> =>
  entries.reduce(
    (acc, entry) => {
      acc[entry.id] = mapDeskAvailabilityToDeskStatus(entry.status.availability)
      return acc
    },
    {} as Record<string, DeskStatus>,
  )

/**
 * Determines the live status of a desk, evaluated against the current moment
 * ("now") for today and against whole-day occupancy for other days. Used by the
 * shareable floor plan, which has no authenticated access to the
 * desks/availability endpoint and so must compute status client-side.
 * The web and mobile floor plans instead read desk status from that backend
 * availability endpoint.
 *
 * @param isActive Whether the desk is active.
 * @param reservations Array of reservations for the desk.
 * @param settings Application settings containing prereservation period, checked-in status, timeslot enforcement, and timezone.
 * @param isBookable Whether the desk is bookable (not blocked by department rules, etc.).
 * @param hasTimeslots Whether the desk has available timeslots (only relevant if timeslots are enforced).
 * @param targetDate The date to check status for (defaults to current date)
 * @returns The status of the desk.
 */
export const getDeskStatusNow = (
  isActive: boolean,
  reservations: Array<{
    start?: string | Date | null
    end?: string | Date | null
    checked_in?: { check_in_at?: string; check_out_at?: string } | null
  }>,
  settings: {
    desk_prereservation_period?: string | number | null
    desk_check_in_enabled?: boolean | null
    desk_force_timeslot_use?: boolean | null
    timezone: string
  },
  isBookable: boolean = true,
  hasTimeslots: boolean = true,
  targetDate: Dayjs = dayjs(),
): DeskStatus => {
  const tz = settings.timezone
  // Use local time since all times are displayed in user's local timezone
  const now = dayjs()
  const isToday = isSameCalendarDay(targetDate, now)

  const referenceTime = now

  // If desk is not active, it's unavailable
  if (!isActive) {
    return DeskStatus.UNAVAILABLE
  }

  // Filter reservations that overlap with the target date
  const dayReservations = reservations.filter((r) => {
    if (!r.start || !r.end) {
      return false
    }

    const start = dayjs(r.start)
    const end = dayjs(r.end)
    const targetDay = targetDate.format("YYYY-MM-DD")

    const startDay = start.format("YYYY-MM-DD")
    const endDay = end.format("YYYY-MM-DD")

    const overlapsTargetDay =
      startDay === targetDay ||
      endDay === targetDay ||
      (startDay < targetDay && endDay > targetDay)

    return overlapsTargetDay
  })

  if (isToday) {
    // A) For current day: check against current time

    // PRIORITY 1: Check if ANY reservation is checked in (even if not started yet)
    // If someone checks in early, desk should show as occupied immediately
    const isCheckInEnabled = settings?.desk_check_in_enabled === true
    if (isCheckInEnabled) {
      const anyReservationCheckedIn = dayReservations.some((r) => {
        if (!r.end) {
          return false
        }

        const end = dayjs(r.end)
        // Only consider reservations that haven't ended yet
        if (end <= referenceTime) {
          return false
        }

        const isCheckedIn = !!r.checked_in?.check_in_at
        const isCheckedOut = !!r.checked_in?.check_out_at
        return isCheckedIn && !isCheckedOut
      })

      if (anyReservationCheckedIn) {
        return DeskStatus.OCCUPIED
      }
    }

    // PRIORITY 2: Check if timeslots are enforced and there are no available timeslots
    const timeslotsEnforced = settings?.desk_force_timeslot_use === true
    if (timeslotsEnforced && !hasTimeslots) {
      return DeskStatus.UNAVAILABLE
    }

    // PRIORITY 3: Check if there are any CURRENT reservations
    const isCurrentlyBooked = dayReservations.some((r) => {
      if (!r.start || !r.end) {
        return false
      }

      const start = dayjs(r.start)
      const end = dayjs(r.end)
      return start <= referenceTime && end > referenceTime
    })

    if (isCurrentlyBooked) {
      // If check-in is not enabled, desk is occupied
      if (!isCheckInEnabled) {
        return DeskStatus.OCCUPIED
      }

      // If check-in is enabled but desk is not checked in, desk is upcoming
      return DeskStatus.UPCOMING
    }

    // PRIORITY 4: Check if desk has an upcoming reservation within the prereservation period
    const isUpcoming = hasPreReservation(
      dayReservations,
      { ...settings, timezone: tz },
      referenceTime,
    )

    if (isUpcoming) {
      return DeskStatus.UPCOMING
    }

    // PRIORITY 5: If desk is not bookable, it's unavailable
    if (!isBookable) {
      return DeskStatus.UNAVAILABLE
    }

    return DeskStatus.AVAILABLE
  } else {
    // B) For future dates: show red if any reservation exists for that day
    if (dayReservations.length > 0) {
      return DeskStatus.OCCUPIED
    }

    return isBookable ? DeskStatus.AVAILABLE : DeskStatus.UNAVAILABLE
  }
}

// Room occupancy is binary; no "upcoming" state unlike desks.
export const RoomStatus = {
  AVAILABLE: "AVAILABLE",
  OCCUPIED: "OCCUPIED",
} as const
export type RoomStatus = (typeof RoomStatus)[keyof typeof RoomStatus]

/**
 * Determines the live status of a room, evaluated against the current moment
 * ("now") for today and against whole-day occupancy for other days. Used by the
 * web and shareable floor plans. The mobile floor plan instead reads room status from the backend availability endpoint.
 *
 * @param reservations Array of reservations for the room.
 * @param targetDate The date to check status for (defaults to current date).
 * @returns The status of the room.
 */
export const getRoomStatusNow = (
  reservations: Array<{
    start?: string | Date | null
    end?: string | Date | null
  }>,
  targetDate: Dayjs = dayjs(),
): RoomStatus => {
  // Use local time since all times are displayed in user's local timezone
  const now = dayjs()
  const isToday = isSameCalendarDay(targetDate, now)

  // If there are no reservations, room is available
  if (!reservations || reservations.length === 0) {
    return RoomStatus.AVAILABLE
  }

  // Filter reservations that overlap with the target date
  const dayReservations = reservations.filter((r) => {
    if (!r.start || !r.end) {
      return false
    }

    const start = dayjs(r.start)
    const end = dayjs(r.end)
    const targetDay = targetDate.format("YYYY-MM-DD")

    const startDay = start.format("YYYY-MM-DD")
    const endDay = end.format("YYYY-MM-DD")

    // Include if: starts on target day OR ends on target day OR spans across target day
    const overlapsTargetDay =
      startDay === targetDay ||
      endDay === targetDay ||
      (startDay < targetDay && endDay > targetDay)

    return overlapsTargetDay
  })

  if (dayReservations.length === 0) {
    return RoomStatus.AVAILABLE
  }

  if (isToday) {
    // A) For current day (using "now")
    const isCurrentlyBooked = dayReservations.some((r) => {
      if (!r.start || !r.end) {
        return false
      }

      const start = dayjs(r.start)
      const end = dayjs(r.end)
      return start <= now && end > now
    })
    return isCurrentlyBooked ? RoomStatus.OCCUPIED : RoomStatus.AVAILABLE
  } else {
    // B) For other days (not current day)
    const dayStart = createDateTime(targetDate, "00:00")
    const dayEnd = createDateTime(targetDate, "23:59")

    // Check if room has a reservation that spans the entire day (i.e., full-day reservation)
    for (const r of dayReservations) {
      if (!r.start || !r.end) {
        continue
      }

      const start = dayjs(r.start)
      const end = dayjs(r.end)

      const coversEntireDay =
        start.isSameOrBefore(dayStart) && end.isSameOrAfter(dayEnd)

      // If reservation covers the entire day, room is occupied
      if (coversEntireDay) {
        return RoomStatus.OCCUPIED
      }
    }

    // If no full-day reservation was found, room is available
    return RoomStatus.AVAILABLE
  }
}

/**
 * Maps backend room availability status to the
 * binary floor-plan RoomStatus. Rooms have no "upcoming" state, so anything
 * other than free (busy/disabled/pending) is treated as occupied/not bookable.
 */
export const mapRoomAvailabilityToRoomStatus = (
  availability: string,
): RoomStatus =>
  availability === "free" ? RoomStatus.AVAILABLE : RoomStatus.OCCUPIED

/**
 * Extracts the resource ID (desk or asset ID) from a reservation object.
 * Uses type guard to determine whether the reservation is for a desk or asset.
 *
 * @param reservation - DeskReservation or AssetReservation object
 * @returns The desk.id or asset.id string
 */
export const getResourceIdFromReservation = (
  reservation: DeskReservation | AssetReservation,
) => ("desk" in reservation ? reservation.desk.id : reservation.asset.id)

/**
 * Finds the user's reservation for a specific resource, regardless of whether
 * it is already running. Check-in screens need this instead of
 * `findMyCurrentReservation` because check-in opens before the reservation
 * starts (see `ReservationCheckinUtils.isEnabled`).
 *
 * @param reservations - Array of the user's DeskReservation or AssetReservation objects
 * @param resourceId - The desk or asset ID to search for
 * @returns The reservation object or undefined if not found
 */
export const findMyReservationForResource = <
  T extends DeskReservation | AssetReservation,
>(
  reservations: T[],
  resourceId: string,
): T | undefined =>
  reservations.find(
    (reservation) => getResourceIdFromReservation(reservation) === resourceId,
  )

/**
 * Finds the user's currently active reservation for a specific resource.
 * Checks both resource ID match and reservation activity status.
 *
 * @param reservations - Array of DeskReservation or AssetReservation objects
 * @param resourceId - The desk or asset ID to search for
 * @returns The active reservation object or undefined if not found
 */
export const findMyCurrentReservation = (
  reservations: (DeskReservation | AssetReservation)[],
  resourceId: string,
) => {
  const now = dayjs()

  return reservations?.find(
    (reservation) =>
      getResourceIdFromReservation(reservation) === resourceId &&
      isReservationActive(reservation, now),
  )
}

/**
 * Checks if a resource is currently reserved by someone else.
 * Verifies that an active reservation exists for the resource
 * with a different user email.
 *
 * @param reservations - Array of DeskReservation or AssetReservation objects
 * @param resourceId - The desk or asset ID to check
 * @param userEmail - Current user's email for comparison
 * @returns true if the resource is actively reserved by another user
 */
export const isResourceReservedBySomeoneElse = (
  reservations: (DeskReservation | AssetReservation)[],
  resourceId: string,
  userEmail: string,
) => {
  const now = dayjs()

  return reservations?.some(
    (reservation) =>
      getResourceIdFromReservation(reservation) === resourceId &&
      reservation.user.email !== userEmail &&
      isReservationActive(reservation, now),
  )
}

/**
 * Checks if the current user has an active reservation for a specific resource.
 * Note: Does not verify user email, assumes reservations array contains only user's reservations.
 *
 * @param reservations - Array of the user's DeskReservation or AssetReservation objects
 * @param resourceId - The desk or asset ID to check
 * @returns true if the user has an active reservation for this resource
 */
export const isResourceActivelyReservedByMe = (
  reservations: (DeskReservation | AssetReservation)[],
  resourceId: string,
) => {
  const now = dayjs()

  return reservations?.some(
    (reservation) =>
      getResourceIdFromReservation(reservation) === resourceId &&
      isReservationActive(reservation, now),
  )
}

/**
 * Checks if the current user is checked in to a specific resource.
 * Requires both an active reservation and a non-null checked_in timestamp.
 *
 * @param reservations - Array of the user's DeskReservation or AssetReservation objects
 * @param resourceId - The desk or asset ID to check
 * @returns true if the user is checked in to an active reservation for this resource
 */
export const isResourceCheckedInByMe = (
  reservations: (DeskReservation | AssetReservation)[],
  resourceId: string,
) => {
  const now = dayjs()

  return reservations?.some(
    (reservation) =>
      getResourceIdFromReservation(reservation) === resourceId &&
      reservation.checked_in !== null &&
      isReservationActive(reservation, now),
  )
}

/**
 * Capitalizes the first letter of a string.
 * @param str - The string to capitalize
 * @returns The capitalized string
 */
export const capitalize = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

/**
 * Strips HTML tags from a string and formats the text for display.
 *
 * This function processes HTML text by:
 * 1. Converting block-level closing tags to newlines
 * 2. Converting self-closing <br> and <hr> tags to newlines
 * 3. Removing all other HTML tags while preserving the text content
 * 4. Cleaning up extra whitespace and newlines
 *
 * The function preserves the semantic structure of the original HTML
 * by maintaining appropriate line breaks between block elements.
 *
 * @param text - The HTML string to be stripped of tags
 * @returns A clean text string with HTML tags removed and proper formatting
 *
 * Example:
 * Input: "<p>Hello</p><br/><div>World!</div>"
 * Output: "Hello\nWorld!"
 */

export function stripHtmlTags(text: string): string {
  let result = text

  // Add new line before closing block tags
  for (let i = 0, len = HTML_BLOCK_TAGS.length; i < len; i++) {
    const tag = HTML_BLOCK_TAGS[i]
    const closingTagRegex = new RegExp(`</${tag}>`, "gi")
    result = result.replace(closingTagRegex, "\n")
  }

  // Handle self-closing br and hr tags
  result = result.replace(/<br\s*\/?>/gi, "\n")

  // Remove all remaining HTML tags
  result = result.replace(/<[^>]*>/g, "")

  // Clean up multiple consecutive newlines
  result = result.replace(/\n\s*\n/g, "\n")

  result = result.replace(/&nbsp;/g, "")

  result = decodeHtml(result)

  // Trim whitespace from start and end
  result = result.trim()
  return result
}

function decodeHtml(input: string): string {
  const txt = document.createElement("textarea")
  txt.innerHTML = input
  return txt.value
}

/**
 * Validates the eTagBadgeAuthKey payload from PostHog.
 * Ensures it's an array of finite integers between 0 and 255.
 * Returns a sanitized number[] if valid, undefined otherwise.
 */

export const getBrandingLogoUrl = (branding?: BrandingResponse) =>
  branding?.company_logo?.url &&
  (branding.company_logo.url.startsWith("http")
    ? branding.company_logo.url
    : `${API_ROOT}${branding.company_logo.url}`)

/** Company logo for badge print when LOGO branding is selected; falls back to Joan landscape logo. */
export const getPrintBrandingLogoUrl = (
  branding: BrandingResponse | undefined,
  brandingOption: BrandingOption,
): string | undefined => {
  if (brandingOption !== BrandingOption.LOGO) {
    return undefined
  }

  const companyLogoUrl = getBrandingLogoUrl(branding)
  return companyLogoUrl || defaultLandscapeLogoUrl
}

export const updateViewportHeight = () => {
  const vh = window.innerHeight / 100
  document.documentElement.style.setProperty("--vh", `${vh}px`)
}

// Emails can arrive with mixed casing from different calendar providers
export const normalizeEventEmail = (o?: string) => {
  return o ? o.toLowerCase() : o
}
